import React, {createContext, useContext, useMemo} from 'react';
import {useLocation, useParams} from 'react-router-dom';

import {usePropsSelector} from '../../state/hooks';
import {Ref as PanelRef} from '../../state/views/panel/types';
import {makePanelRefsSelector} from '../../state/views/panelBankConfig/selectors';
import {usePanelBankSectionConfigMaybe} from '../../state/views/panelBankSectionConfig/hooks';
import type {Ref as PanelBankSectionConfigRef} from '../../state/views/panelBankSectionConfig/types';
import {LayedOutPanel} from '../../util/panelTypes';
import {SectionWithRef} from '../PanelBank/types';
import {getTrimmedTitleFromAnyConfig} from '../PanelTitle/getTitleFromConfig';
import {useWorkspaceRefsContext} from '../Workspace/WorkspaceRefsContext';
import {useDetectPanelVariant} from './hooks';
import {usePanelContext} from './PanelContextProvider';

export const usePanelRefAndPanelBankSectionConfigRef = (panelId: string) => {
  const {panelBankConfigRef} = useWorkspaceRefsContext();

  const panelRefAndPanelBankSectionConfigRef = usePropsSelector(
    makePanelRefsSelector,
    panelBankConfigRef,
    {panelId}
  );
  return panelRefAndPanelBankSectionConfigRef;
};

export const usePanelRefAndPanelBankSectionConfigRefByName = (
  panelDisplayName: string,
  panelSectionName?: string
) => {
  const {panelBankConfigRef} = useWorkspaceRefsContext();

  const panelRefAndPanelBankSectionConfigRef = usePropsSelector(
    makePanelRefsSelector,
    panelBankConfigRef,
    {
      displayName: panelDisplayName,
      sectionName: panelSectionName,
    }
  );
  return panelRefAndPanelBankSectionConfigRef;
};

export const getSharePanelLink = (
  panel: LayedOutPanel,
  panelBankSectionConfig: SectionWithRef | undefined
) => {
  const pathname = window.location.pathname.replace(/\/+$/, ''); // strip trailing slashes
  const searchParams = new URLSearchParams(window.location.search);
  const panelType = panel.viewType;
  let newUrl = '';
  const panelDisplayName = getTrimmedTitleFromAnyConfig(panelType, panel);
  if (panel.isAuto && panelDisplayName) {
    searchParams.set('panelDisplayName', panelDisplayName);
    if (panelBankSectionConfig?.name != null) {
      searchParams.set('panelSectionName', panelBankSectionConfig.name);
    }
    newUrl = `${pathname}?${searchParams.toString()}`;
  } else {
    newUrl = `${pathname}/panel/${panel.__id__}?${searchParams.toString()}`;
  }
  return newUrl;
};

export const useSharePanelLink = () => {
  const {panel, panelBankSectionConfigRef} = usePanelContext();
  const panelBankSectionConfig = usePanelBankSectionConfigMaybe(
    panelBankSectionConfigRef
  );

  return () => getSharePanelLink(panel, panelBankSectionConfig);
};

type StandalonePanelContextProviderProps = {
  children: React.ReactNode;
};

type StandalonePanelContextType = {
  panelRefAndPanelBankSectionConfigRef: {
    panelRef: PanelRef;
    panelBankSectionConfigRef: PanelBankSectionConfigRef;
  } | null;
  isPanelNotFound: boolean;
};

const StandalonePanelContext = createContext<
  StandalonePanelContextType | undefined
>(undefined);

export const StandalonePanelContextProvider = ({
  children,
}: StandalonePanelContextProviderProps) => {
  const location = useLocation();
  const {
    isFullScreenPanelById,
    isFullScreenPanelByName,
    isFullScreenPanelByNameAndSectionName,
  } = useDetectPanelVariant();
  const {panelId} = useParams<{panelId: string}>();
  const searchParams = new URLSearchParams(location.search);
  const panelDisplayName = searchParams.get('panelDisplayName');
  const panelSectionName = searchParams.get('panelSectionName');

  const panelRefAndPanelBankSectionConfigRefFromPanelId =
    usePanelRefAndPanelBankSectionConfigRef(panelId ?? '');

  const panelRefAndPanelBankSectionConfigRefFromDisplayName =
    usePanelRefAndPanelBankSectionConfigRefByName(
      panelDisplayName ?? '',
      panelSectionName ?? ''
    );

  let panelRefAndPanelBankSectionConfigRef: {
    panelRef: PanelRef;
    panelBankSectionConfigRef: PanelBankSectionConfigRef;
  } | null = null;
  if (isFullScreenPanelById) {
    panelRefAndPanelBankSectionConfigRef =
      panelRefAndPanelBankSectionConfigRefFromPanelId;
  }
  if (isFullScreenPanelByName || isFullScreenPanelByNameAndSectionName) {
    panelRefAndPanelBankSectionConfigRef =
      panelRefAndPanelBankSectionConfigRefFromDisplayName;
  }

  const value = useMemo(
    () => ({
      panelRefAndPanelBankSectionConfigRef,
      isPanelNotFound: !panelRefAndPanelBankSectionConfigRef,
    }),
    [panelRefAndPanelBankSectionConfigRef]
  );

  return (
    <StandalonePanelContext.Provider value={value}>
      {children}
    </StandalonePanelContext.Provider>
  );
};

export const useStandalonePanelContext = () => {
  const value = useContext(StandalonePanelContext);

  if (value == null) {
    throw new Error(
      'useFullScreenPanelNotFound must be used within a FullScreenPanelNotFound'
    );
  }

  return value;
};
